// #region: Class definition *********************************************************************/
/**
 * @class ToggleButton
 * @description Custom web component that renders a 'toggle-button' component from an existing checkbox.
 * 
 * @extends WebComponent
 * 
 * @example
 * <toggle-button>
 *    <input type="checkbox" />
 * </toggle-button>
 */
 
class ToggleButton extends WebComponent {

    // #region: Private Fields *******************************************************************/
    /**
     * The checkbox input element that this toggle button wraps.
     * @type {HTMLInputElement|null}
     * 
     * @private
     */
    #checkbox;
    // #endregion ********************************************************************************/

    // #region: Constructor **********************************************************************/
    /**
     * Creates an instance of ToggleButton.
     */
    constructor() {
        super();

        this.#checkbox = this.querySelector('input[type="checkbox"]');
        this.#checkbox.addEventListener('change', this.onChange.bind(this));

        // Create the toggle.
        const toggle = document.createElement('div');
        toggle.classList.add('toggle');
        this.appendChild(toggle);
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Registered tag name for this web component.
     * This is used by the browser to identify and register the custom element.
     * @type {string}
     * @static
     * @memberof ToggleButton
     */
    static tagName = 'toggle-button';

    /**
     * Identifies class instance.
     * @returns {string} The string representation of this class instance.
     * 
     * @memberof ToggleButton
     */
    toString() {
        return '[ToggleButton]';
    }

    /**
     * Identifies class.
     * @returns {string} The string representation of this class.
     * 
     * @static
     * @memberof ToggleButton
     */
    static toString() {
        return '[ToggleButton]';
    }

    /**
     * Identifies the version.
     * @returns {string} The version number of this class.
     * 
     * @static
     * @memberof ToggleButton
     */
    static version() {
        return '1.0.0';
    }
    // #endregion ********************************************************************************/

    // #region: Properties ***********************************************************************/
    /**
     * Gets the current state of the toggle button.
     * @returns {boolean} True if checked, otherwise false.
     * 
     * @memberof ToggleButton
     */
    get checked() {
        return this.#checkbox.checked;
    }

    /**
     * Sets the state of the toggle button.
     * @param {boolean} value True to check, false to uncheck.
     * 
     * @memberof ToggleButton
     */
    set checked(value) {
        this.#checkbox.checked = value;
        this.onChange();
    }
    // #endregion ********************************************************************************/

    // #region: Lifecycle events *****************************************************************/
    /**
     * Called when the custom element is attached to the DOM.
     * 
     * @memberof ToggleButton
     */
    connectedCallback() {
        super.connectedCallback();

        // Add keyboard handling.
        this.addEventListener('keydown', this.onKeyDown.bind(this));
    }

    /**
     * Cleanup. Called when the component is removed from the DOM.
     * 
     * @memberof ToggleButton
     */
    disconnectedCallback() {
        super.disconnectedCallback();

        this.#checkbox.removeEventListener('change', this.onChange.bind(this));
        this.removeEventListener('keydown', this.onKeyDown.bind(this));
    }
    // #endregion ********************************************************************************/

    // #region: Events ***************************************************************************/
    /**
     * Handles the change event on the checkbox.
     * 
     * @memberof ToggleButton
     */
    onChange() {
        if (this.#checkbox.checked) {
            this.setAttribute('active', '');
        }
        else {
            this.removeAttribute('active');
        }
    }

    /**
     * Handles keyboard events directly in the component.
     * @param {KeyboardEvent} event The keyboard event.
     * 
     * @memberof ToggleButton
     */
    onKeyDown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();

            // Toggle checkbox.
            this.checked = !this.checked;
        }
    }
    // #endregion ********************************************************************************/

    // #region: Public Methods *******************************************************************/
    /**
     * Validates the content.
     * @returns {boolean} True if valid, false if not. Defaults to true.
     * 
     * @memberof ToggleButton
     */
    validate() {
        return true;
    }
    // #endregion ********************************************************************************/

}
// #endregion ************************************************************************************/

/**
 * Register web component.
 * Defines the custom element 'toggle-button' and maps it to the ToggleButton class.
 */
customElements.define(ToggleButton.tagName, ToggleButton);
